import {
  Alert,
  Box,
  Grid,
  Paper,
  LinearProgress,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import moment from 'moment'
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import API from '../../../services/API'
import AddDrugsToStore from './AddDrugsToStore'
import Contents from './Contents'
import LocationBanner from './LocationBanner'
import Log from '../../../components/Logs/Log'
import MedicineDrawer from './MedicineDrawer'
import getMedicineAlarm from './getMedicineAlarm'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'

const MedicineLocation = () => {
  const [loading, setLoading] = useState(true)
  const { locationId } = useParams()
  const {setPageTitle} = useDocumentTitle()
  const [location, setLocation] = useState({})
  const [logs, setLogs] = useState([])
  const [contents, setContents] = useState([])
  const [contentSummary, setContentSummary] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [enterDrugsToStore, setEnterDrugsToStore] = useState(false)
  const [selectedMedicines, setSelectedMedicines] = useState([])
  const [requiredDrugs, setRequiredDrugs] = useState([])
  const [bagLevels, setBagLevels] = useState([])
  const [toast, setToast] = useState()

  const contentsSummaryFn = () => {
    try {
      let Ar = []
      for (const drug of contents) {
        const i = Ar.findIndex((e) => e._id === drug.medicine._id)
        if (i > -1) {
          Ar[i].qty++
          Ar[i].expiry =
            moment(Ar[i].expiry) < moment(drug.expiry)
              ? Ar[i].expiry
              : drug.expiry
        } else {
          Ar.push({
            name: drug.medicine.title,
            _id: drug.medicine._id,
            constitution: drug.medicine?.constitution,
            presentation: drug.medicine?.presentation,
            expiry: drug.expiry,
            qty: 1,
            isControlled: drug?.medicine?.isControlled ?? false,
            alarm: getMedicineAlarm(drug.medicine, location, bagLevels),
          })
        }
      }
      const sortedAr = Ar.sort((a, b) =>
        a.name > b.name ? 1 : a.name < b.name ? -1 : 0,
      )
      setContentSummary(sortedAr)
    } catch {
      setContentSummary([])
    }
  }

  const loadPageAPI = () => {
    setLoading(true)
    API.get(`/medicines-management/location?id=${locationId}`)
      .then((res) => {
        setLocation(res.data.location)
        setLogs(res.data.logs)
        setContents(res.data.contents)
        setRequiredDrugs(res.data.requiredDrugs)
        setBagLevels(res.data.bagLevels ?? [])
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadPageAPI()
  }, [])

  useEffect(() => {
    contentsSummaryFn()
  }, [contents, location, bagLevels])

  useEffect(() => {
    if (location?.name) {
      setPageTitle(location.name)
    }
  }, [location?.name, setPageTitle])

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ pb: 3, maxWidth: 1680, mx: 'auto', width: '100%' }}
    >
      <Snackbar
        open={!!toast}
        onClose={() => setToast()}
        autoHideDuration={4000}
      >
        <Alert severity={toast?.severity}>{toast?.message}</Alert>
      </Snackbar>
      {loading ? (
        <LinearProgress />
      ) : fetchError ? (
        <Alert severity="error">
          Unable to retrieve information. Please try again later.
        </Alert>
      ) : (
        <>
          <LocationBanner
            location={location}
            setEnterDrugsToStore={setEnterDrugsToStore}
            contentSummary={contentSummary}
            contents={contents}
            loadPageAPI={loadPageAPI}
          />
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12}>
              <Contents
                contentSummary={contentSummary}
                setSelectedMedicines={setSelectedMedicines}
                allDrugs={contents}
                requiredDrugs={requiredDrugs}
                emptyHint={
                  location?.type === 'Medicine Store'
                    ? 'Use the "Add stock" button above to enter medicines into this store — you can then assign them out to bags from here.'
                    : 'Assign medicines to this bag from a medicine store — open the store, select a medicine, and choose "Assign to a bag".'
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Log logs={logs} title="Activity" />
            </Grid>
          </Grid>
          <AddDrugsToStore
            modalOpen={enterDrugsToStore}
            setModalOpen={setEnterDrugsToStore}
            location={location}
            setToast={setToast}
            loadPageAPI={loadPageAPI}
          />
          <MedicineDrawer
            selectedMedicines={selectedMedicines}
            setSelectedMedicines={setSelectedMedicines}
            location={location}
            loadPageAPI={loadPageAPI}
          />
        </>
      )}
    </Box>
  )
}

export default MedicineLocation
